import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { reviewService } from '../../_services/review.service';
import BeuLoader from '../common/BeuLoader';
import './DashboardReviews.css';

// Back Arrow Icon
const BackArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

// Icons
const StarIcon = ({ filled = true }) => (
  <svg className={`review-star ${filled ? 'filled' : 'empty'}`} viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const VerifiedIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="verified-icon">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
  </svg>
);

const EmptyReviewsIcon = () => (
  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <path d="M8 10h.01M12 10h.01M16 10h.01" strokeLinecap="round" />
  </svg>
);

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// Helper function to get initials
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Review Card Component
const ReviewCard = ({ review }) => {
  const [expanded, setExpanded] = useState(false);
  const reviewText = review.reviewText || review.comment || review.review || '';
  const shouldTruncate = reviewText && reviewText.length > 200;
  
  // Get entity info (salon or hairdresser)
  // The backend populates salonId and hairdresserId fields with the entity data
  // hairdresserId now references User model directly (consistent with Booking model)
  const getHairdresserName = () => {
    // hairdresserId is now populated directly with User data
    if (review.hairdresserId?.name) return review.hairdresserId.name;
    if (review.hairdresserId?.first_name) {
      const firstName = review.hairdresserId.first_name;
      const lastName = review.hairdresserId.last_name || '';
      return `${firstName} ${lastName}`.trim();
    }
    // Fallback to hairdresser object (legacy)
    if (review.hairdresser?.name) return review.hairdresser.name;
    return null;
  };
  
  const getSalonName = () => {
    // Try salonId first (populated)
    if (review.salonId?.salonName) return review.salonId.salonName;
    // Try booking's salonId
    if (review.bookingId?.salonId?.salonName) return review.bookingId.salonId.salonName;
    // Fallback to salon object
    if (review.salon?.salonName) return review.salon.salonName;
    return null;
  };
  
  const entityName = review.reviewType === 'salon'
    ? (getSalonName() || 'Unknown Salon')
    : (getHairdresserName() || 'Unknown Stylist');
    
  // hairdresserId now directly contains User data (not nested)
  // v2 API returns full S3 URLs for images
  const entityImage = review.salonId?.image || review.salonId?.salonImage ||
                      review.hairdresserId?.userImage?.circleImage ||
                      review.hairdresserId?.userImage?.rectangleImage ||
                      review.hairdresserId?.userImage?.squareImage ||
                      review.bookingId?.salonId?.salonImage ||
                      review.bookingId?.salonId?.image ||
                      review.salon?.salonImage ||
                      review.salon?.image ||
                      review.hairdresser?.userImage?.circleImage ||
                      null;
  const reviewType = review.reviewType || (review.salonId || review.salon ? 'salon' : 'hairdresser');
  
  // Get services from booking if available
  const services = review.bookingId?.servicesBooked || review.booking?.servicesBooked || review.services || [];
  
  return (
    <div className="my-review-card">
      <div className="my-review-card-header">
        {entityImage ? (
          <img 
            src={entityImage} 
            alt={entityName} 
            className="my-review-card-avatar"
          />
        ) : (
          <div className="my-review-card-avatar-initials">
            {getInitials(entityName)}
          </div>
        )}
        <div className="my-review-card-info">
          <h4 className="my-review-card-name">{entityName}</h4>
          <span className="my-review-card-type">
            {reviewType === 'salon' ? 'Salon Review' : 'Stylist Review'}
          </span>
          <div className="my-review-card-meta">
            <div className="my-review-card-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <StarIcon key={star} filled={star <= review.rating} />
              ))}
            </div>
            <span className="my-review-card-date">{formatDate(review.createdAt)}</span>
            <span className="my-review-card-verified">
              <VerifiedIcon />
              Verified
            </span>
          </div>
        </div>
      </div>
      
      {reviewText && (
        <div className="my-review-card-content">
          <p className={`my-review-card-text ${shouldTruncate && !expanded ? 'truncated' : ''}`}>
            {reviewText}
          </p>
          {shouldTruncate && (
            <button 
              className="my-review-card-read-more"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      )}
      
      {/* Only services with a name: an unnamed one drew an empty grey pill */}
      {(() => {
        const names = services.map(s => (typeof s === 'string' ? s : (s && (s.service?.serviceName || s.serviceName)) || '')).map(n => String(n).trim()).filter(Boolean);
        return names.length > 0 && (
          <div className="my-review-card-services">
            {names.map((name, index) => <span key={index} className="my-review-card-service-tag">{name}</span>)}
          </div>
        );
      })()}
      
      {(review.reply || review.ownerResponse) && (
        <div className="my-review-card-response">
          <div className="my-review-card-response-header">
            <span className="my-review-card-response-label">Response from {entityName}</span>
          </div>
          <p className="my-review-card-response-text">{review.reply || review.ownerResponse}</p>
        </div>
      )}
      
      {(review.bookingId || review.booking) && (
        <div className="my-review-card-booking-link">
          <Link to={`/dashboard/bookings`} className="view-booking-link">
            View Booking Details →
          </Link>
        </div>
      )}
    </div>
  );
};

const DashboardReviews = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);
  
  const LIMIT = 10;

  // Handle back navigation
  const handleBack = () => {
    history.push('/dashboard/account');
  };

  // Fetch reviews
  const fetchReviews = useCallback(async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setSkip(0);
      } else {
        setLoadingMore(true);
      }
      
      const currentSkip = reset ? 0 : skip;
      const response = await reviewService.getUserReviews(currentSkip, LIMIT);
      
      const data = response.responseData || response.data || response;
      const newReviews = data.reviews || [];
      const count = data.count || data.total || 0;
      
      if (reset) {
        setReviews(newReviews);
      } else {
        setReviews(prev => [...prev, ...newReviews]);
      }
      
      setTotalCount(count);
      setHasMore(currentSkip + newReviews.length < count);
      setSkip(currentSkip + newReviews.length);
      setError(null);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load reviews. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [skip]);

  // Initial fetch
  useEffect(() => {
    fetchReviews(true);
  }, []);

  // Handle load more
  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchReviews(false);
    }
  };

  // Calculate stats
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="dashboard-fade-in">
      <div className="dashboard-page-header">
        <button className="dashboard-back-button" onClick={handleBack}>
          <BackArrowIcon />
        </button>
        <div className="dashboard-page-header-text">
          <h1 className="dashboard-page-title">My Reviews</h1>
          <p className="dashboard-page-subtitle">Reviews you've written for salons and stylists</p>
        </div>
      </div>

      {/* Stats Summary */}
      {!loading && reviews.length > 0 && (
        <div className="my-reviews-stats">
          <div className="my-reviews-stat-card">
            <span className="stat-value">{totalCount}</span>
            <span className="stat-label">Total Reviews</span>
          </div>
          <div className="my-reviews-stat-card">
            <span className="stat-value">{averageRating}</span>
            <span className="stat-label">Average Rating</span>
          </div>
        </div>
      )}

      {/* Reviews List */}
      <div className="my-reviews-container">
        {loading ? (
          <div className="my-reviews-loading">
            <BeuLoader message="Gathering your thoughts" subtitle="Loading your reviews..." icon="star" fullScreen={false} />
          </div>
        ) : error ? (
          <div className="my-reviews-error">
            <p>{error}</p>
            <button onClick={() => fetchReviews(true)} className="retry-button">
              Try Again
            </button>
          </div>
        ) : reviews.length === 0 ? (
          <div className="my-reviews-empty">
            <EmptyReviewsIcon />
            <h3>No reviews yet</h3>
            <p>After your appointments, you can leave reviews for the salons and stylists you've visited.</p>
            <Link to="/dashboard/bookings" className="browse-bookings-link">
              View Your Bookings
            </Link>
          </div>
        ) : (
          <>
            <div className="my-reviews-list">
              {reviews.map((review, index) => (
                <ReviewCard key={review._id || index} review={review} />
              ))}
            </div>
            
            {hasMore && (
              <div className="my-reviews-load-more">
                {loadingMore ? (
                  <div className="loading-more-text">Loading more reviews...</div>
                ) : (
                  <button
                    className="load-more-button"
                    onClick={handleLoadMore}
                  >
                    Load More Reviews
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardReviews;
